import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { TasksController } from "../../../api/src/tasks/tasks.controller";
import type { Task } from "../../../api/src/tasks/tasks.service";
import Loading from "../Loading";
import { Table, useTable } from "../components/Table/Table";
import {
  AssignedToFilter,
  CategoryFilter,
  multiSelectIncludes,
  PriorityFilter,
  priorityOptions,
} from "../components/Table/filters";
import { useQueryCrmUsers } from "../firm/lib";
import { useAuthenticatedFetch } from "../lib/api";

/**
 * The CRM deep link for a task row, or `undefined` when the row cannot be
 * addressed. Both the Name and Description cells go through this, so a single
 * row's two links can no longer point at different CRMs (APP-1413).
 *
 * `Task.service` is a three-valued union, so this switches on every arm
 * explicitly and falls through to `undefined` — the previous
 * `wealthbox ? … : redtail` ternary sent Slant rows (and any row with no
 * `service`) to a Redtail URL built from a non-Redtail id.
 *
 * INTERIM — Slant rows point at the household the task belongs to, using the
 * URL form already proven by the header's "View in Slant" link
 * (clients/household/HouseholdHeader.tsx). Slant has not given us a per-task
 * URL yet; when it does, only the `slant` arm below changes.
 */
export function taskHref(task: Task, slantBookId?: string): string | undefined {
  switch (task.service) {
    case "wealthbox":
      return `${process.env.REACT_APP_WEALTHBOX_APP_ROOT}/tasks/${task.id}`;
    case "redtail":
      return `${process.env.REACT_APP_REDTAIL_APP_ROOT}/activities/${task.id}`;
    case "slant": {
      // The Slant household id is already on the row: slantTaskToTask maps the
      // task's `record` onto linkedTo (api/src/slant/lib.ts). The book id is
      // not, so it comes from the household the list is rendered for.
      const slantHouseholdId = task.linkedTo.find(
        (link) => link.type === "household",
      )?.id;
      return typeof slantBookId === "undefined" ||
        typeof slantHouseholdId === "undefined" ||
        !process.env.REACT_APP_SLANT_APP_ROOT
        ? undefined
        : `${process.env.REACT_APP_SLANT_APP_ROOT}/${slantBookId}/clients/${slantHouseholdId}`;
    }
    default:
      return undefined;
  }
}

/**
 * A task cell's text, linked to its CRM when the row has a resolvable URL. A
 * cell with no text shows no link (APP-2244); the cell stays empty.
 */
const TaskCellLink = ({
  href,
  children,
}: {
  href?: string;
  children?: string | null;
}) =>
  typeof href === "undefined" || !children ? (
    <>{children}</>
  ) : (
    <a href={href} rel="noreferrer" target="_blank">
      {children}
    </a>
  );

const TaskList = ({
  tasks,
  hideAssignee = false,
  slantBookId,
}: {
  tasks: Task[];
  hideAssignee?: boolean;
  /**
   * The Slant book the rendered household belongs to (`Household.slantBookId`).
   * Required to address a Slant task row; absent on non-Slant surfaces.
   */
  slantBookId?: string;
}) => {
  const {
    isPending: isPendingAssignees,
    isError: isErrorAssignees,
    data: dataAssignees,
  } = useQueryCrmUsers({ enabled: !hideAssignee });

  const {
    isPending: isPendingCategories,
    isError: isErrorCategories,
    data: dataCategories,
  } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<TasksController["getCategories"]>>
  >("/tasks/categories");

  const tasksWithAssignedUsers = useMemo(
    () =>
      tasks.map((task) => ({
        ...task,
        assignedUser:
          dataAssignees?.data.find(
            (assignee) => assignee.id === task.assignedTo,
          ) ?? null,
      })),
    [dataAssignees?.data, tasks],
  );

  const columnHelper = useMemo(() => createColumnHelper<Task>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => (
          <TaskCellLink href={taskHref(info.row.original, slantBookId)}>
            {info.getValue()}
          </TaskCellLink>
        ),
        header: () => "Name",
      }),
      columnHelper.accessor("description", {
        cell: (info) => (
          <TaskCellLink href={taskHref(info.row.original, slantBookId)}>
            {info.getValue()}
          </TaskCellLink>
        ),
        header: () => "Description",
      }),
      columnHelper.accessor(
        (row) => {
          const category = dataCategories?.data.find(
            (a) => a.id === row.category,
          );

          return category?.name ?? "";
        },
        {
          id: "category",
          cell: (info) =>
            isPendingCategories ? (
              <Loading />
            ) : isErrorCategories ? (
              <span className="text-danger">Error</span>
            ) : (
              info.getValue()
            ),
          header: () => "Category",
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: CategoryFilter,
          },
        },
      ),
      ...(hideAssignee
        ? []
        : [
            columnHelper.accessor(
              (row) => row.assignedUser?.id.toString() ?? "",
              {
                id: "assignedTo",
                cell: (info) =>
                  isPendingAssignees ? (
                    <Loading />
                  ) : isErrorAssignees ? (
                    <span className="text-danger">Error</span>
                  ) : (
                    (info.row.original.assignedUser?.name ?? "")
                  ),
                header: () => "Assignee",
                filterFn: multiSelectIncludes,
                meta: {
                  filterComponent: AssignedToFilter,
                },
              },
            ),
          ]),
      columnHelper.accessor(
        (row) => {
          const priority = priorityOptions.find(
            (a) => a.label === row.priority,
          );
          return priority?.value.toString() ?? "";
        },
        {
          id: "priority",
          cell: (info) => {
            const priority = priorityOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return priority?.label ?? "";
          },
          header: () => "Priority",
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: PriorityFilter,
          },
        },
      ),
      columnHelper.accessor("dueDate", {
        cell: (info) => info.getValue()?.toLocaleDateString() ?? "",
        header: () => "Due Date",
        enableColumnFilter: false,
      }),
    ],
    [
      dataCategories?.data,
      columnHelper,
      hideAssignee,
      isErrorAssignees,
      isErrorCategories,
      isPendingAssignees,
      isPendingCategories,
      slantBookId,
    ],
  );

  const { table } = useTable({
    columns,
    data: tasksWithAssignedUsers,
    getRowId: (row) => row.id.toString(),
    initialState: {
      sorting: [{ id: "dueDate", desc: false }],
    },
  });

  return <Table table={table} />;
};

export default TaskList;
